import { useMemo, useState } from "react";
import { GitCompareArrows, Layers, ChartScatter, TrendingUp, ChartColumn, Hourglass, RefreshCw, type LucideIcon } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../../../hooks/useSectionParallax";
import { posthog } from "../../../../lib/posthog";
import { Legend, SERIES } from "./ChartKit";
import { CompareChart } from "./CompareChart";
import { ScatterChart } from "./ScatterChart";
import { SurvivalChart } from "./SurvivalChart";
import { GroupedBarChart } from "./GroupedBarChart";
import { BuildAnalysis, METHODS, type Analysis, type AnalysisKey } from "./playgroundData";

const TABS: { key: AnalysisKey; label: string; block: string; icon: LucideIcon }[] = [
  { key: "two-groups", label: "Compare two groups", block: "Compare block", icon: GitCompareArrows },
  { key: "multi-groups", label: "Compare 3+ groups", block: "Compare block", icon: Layers },
  { key: "correlation", label: "Correlation", block: "Correlation block", icon: ChartScatter },
  { key: "regression", label: "Regression", block: "Model block", icon: TrendingUp },
  { key: "categorical", label: "Categorical data", block: "Compare block", icon: ChartColumn },
  { key: "survival", label: "Time to event", block: "Kaplan–Meier chart", icon: Hourglass },
];

function AnalysisChart({ analysis }: { analysis: Analysis }) {
  switch (analysis.chart) {
    case "box": return <CompareChart groups={analysis.groups} />;
    case "scatter": return <ScatterChart points={analysis.points} curve={analysis.curve} xLabel={analysis.xLabel} yLabel={analysis.yLabel} yDomain={analysis.yDomain} />;
    case "bars": return <GroupedBarChart categories={analysis.categories} counts={analysis.counts} />;
    case "survival": return <SurvivalChart curves={analysis.curves} maxTime={analysis.maxTime} />;
  }
}

function TabList({ active, onSelect }: { active: AnalysisKey; onSelect: (k: AnalysisKey) => void }) {
  return (
    <div role="tablist" aria-label="Statistical analyses" className="flex lg:flex-col gap-2 overflow-x-auto pb-1 lg:pb-0">
      {TABS.map(({ key, label, block, icon: Icon }) => (
        <button
          key={key} role="tab" aria-selected={active === key} onClick={() => onSelect(key)}
          className={`shrink-0 flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${active === key ? "border-[#1E4FD8]/50 dark:border-[#4A90D9]/50 bg-[#1E4FD8]/5 dark:bg-[#4A90D9]/10" : "border-[#0A0A0F]/10 dark:border-white/10 hover:border-[#0A0A0F]/25 dark:hover:border-white/25"}`}
        >
          <Icon size={18} className={active === key ? "text-[#1E4FD8] dark:text-[#4A90D9]" : "text-[#5C5C66] dark:text-[#9A9A9A]"} />
          <span>
            <span className="block text-sm font-medium text-[#0A0A0F] dark:text-white">{label}</span>
            <span className="block text-[11px] text-[#5C5C66] dark:text-[#9A9A9A]">{METHODS[key].length > 1 ? `${METHODS[key].length} methods · ${block}` : block}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function MethodChips({ methods, active, onSelect }: { methods: string[]; active: number; onSelect: (i: number) => void }) {
  if (methods.length < 2) return <span className="text-xs font-medium text-[#0A0A0F] dark:text-white">{methods[0]}</span>;
  return (
    <div role="radiogroup" aria-label="Method" className="inline-flex flex-wrap rounded-full border border-[#0A0A0F]/10 dark:border-white/10 p-1">
      {methods.map((m, i) => (
        <button
          key={m} role="radio" aria-checked={active === i} onClick={() => onSelect(i)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${active === i ? "bg-[#0A0A0F] text-white dark:bg-white dark:text-[#050505]" : "text-[#5C5C66] dark:text-[#9A9A9A] hover:text-[#0A0A0F] dark:hover:text-white"}`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function ResultsTable({ analysis }: { analysis: Analysis }) {
  return (
    <table className="w-full text-sm">
      <caption className="text-left text-xs font-bold uppercase tracking-widest text-[#0A0A0F] dark:text-white mb-3">Output</caption>
      <tbody>
        {analysis.rows.map(([label, value]) => (
          <tr key={label} className="border-b border-[#0A0A0F]/10 dark:border-white/10 last:border-0">
            <th scope="row" className="py-2.5 pr-4 text-left font-normal text-[#5C5C66] dark:text-[#9A9A9A]">{label}</th>
            <td className="py-2.5 text-right font-mono text-xs text-[#0A0A0F] dark:text-white">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface CardProps { analysis: Analysis; chartKey: string; methods: string[]; method: number; onMethod: (i: number) => void; onResample: () => void }

function PlaygroundCard({ analysis, chartKey, methods, method, onMethod, onResample }: CardProps) {
  return (
    <div className="min-w-0 rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] p-5 md:p-7 grid md:grid-cols-[1fr_240px] gap-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <MethodChips methods={methods} active={method} onSelect={onMethod} />
          <button onClick={onResample} className="inline-flex items-center gap-1.5 rounded-full border border-[#0A0A0F]/15 dark:border-white/15 px-3 py-1.5 text-xs font-medium text-[#0A0A0F] dark:text-white hover:border-[#1E4FD8] dark:hover:border-[#4A90D9] transition-colors">
            <RefreshCw size={12} /> Resample
          </button>
        </div>
        {analysis.legend && <div className="mb-2"><Legend items={analysis.legend.map((label, i) => ({ label, swatch: SERIES[i].swatch }))} /></div>}
        {/* Keyed so hover state resets when the tab, method, or sample changes. */}
        <AnalysisChart key={chartKey} analysis={analysis} />
      </div>
      <ResultsTable analysis={analysis} />
    </div>
  );
}

export function StatPlayground() {
  const { ref, bgY, fgY } = useSectionParallax();
  const [active, setActive] = useState<AnalysisKey>("two-groups");
  const [method, setMethod] = useState(0);
  const [seed, setSeed] = useState(7);
  const analysis = useMemo(() => BuildAnalysis(active, method, seed), [active, method, seed]);
  const selectTab = (k: AnalysisKey) => { setActive(k); setMethod(0); posthog.capture("iviz_playground_tab", { analysis: k }); };
  const selectMethod = (i: number) => { setMethod(i); posthog.capture("iviz_playground_method", { analysis: active, method: METHODS[active][i] }); };
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }} className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E4FD8] dark:text-[#4A90D9] mb-3">Try it · Live statistics</p>
        <h3 className="text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white mb-4">See the output, not the code.</h3>
        <p className="text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
          Pick an analysis and a method. The chart and the numbers are computed right here in your
          browser on sample data. Hit Resample to draw a new sample.
        </p>
      </motion.div>
      <motion.div style={{ y: bgY }} className="grid lg:grid-cols-[240px_1fr] gap-5 max-w-6xl mx-auto">
        <div className="min-w-0 flex flex-col gap-4">
          <TabList active={active} onSelect={selectTab} />
          <p className="text-xs leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">Illustrative sample data. In iViz, the same analyses run on your own datasets.</p>
        </div>
        <PlaygroundCard analysis={analysis} chartKey={`${active}-${method}-${seed}`} methods={METHODS[active]} method={method} onMethod={selectMethod} onResample={() => setSeed((s) => s + 1)} />
      </motion.div>
    </div>
  );
}
